import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { MaterialIdentifierModel } from '../low-level/material-identifier.model';
import { DateModel } from '../primitive/date.model';
import { NumberModel } from '../primitive/number.model';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { ODataMatStockAvailabilityModel } from './material-stock.model';

export class PartModel extends CompositeModel {
    public materialIdentifier: MaterialIdentifierModel;
    public prevOrder: NumberModel;
    public descText: TextModel;
    public quantity: NumberModel;
    public matMerkmal: TextModel;
    public extUnit: TextModel;
    public amount: NumberModel;
    public batchNumber: TextModel;
    public awbnr: TextModel;
    public sparePartText: TextModel;
    public strahler: TextModel;
    public returnBy: DateModel;
    public orderNrOld: TextModel;
    public xRayDateOld: DateModel;
    public repairPrice: NumberModel;
    public customerPrice: NumberModel;
    public cost: NumberModel;
    public tubeIndicator: TextModel;
    public radioActIndicator: TextModel;
    public serialPIndicator: TextModel;
    public localScrapReturnC: TextModel;
    public localScrapReturnD: TextModel;
    public localScrapReturnV: TextModel;
    public localScrapReturnX: TextModel;
    public lsCrChangeable: TextModel;
    public pos: NumberModel;
    public currency: TextModel;
    public customerCurrency: TextModel;
    public eodDate: DateModel;
    public eosDate: DateModel;
    public ucrNumber: TextModel;
    public unNumber: string;
    public dangerousGoodsIndicator: TextModel;
    public hazardousMaterial: TextModel;
    public storageLocation: TextModel;
    public storageLocationDesc: TextModel;
    public dangerousGoodsText: TextModel;
    public hazardousMaterialText: TextModel;
    public billing: ConfigConstants.BILLABLE_CODE;
    public billingChangeable: boolean;
    public serialNumberOld: TextModel;
    public serialNumberNew: TextModel;
    public readonly textLine: string;
    public repairable: boolean;
    public qualityPart: string;
    public batchNumberLegacy: string;
    public spqDocID: string;
    public enableFDA2579: boolean;
    public isInBootstock: boolean;
    public isMedPhysicsRelevant: boolean;

    public constructor(
        orderNr: string,
        orderPos: string,
        materialNr: string,
        prevOrder: number | undefined,
        descText: string,
        quantity: number,
        matMerkmal: string,
        extUnit: string,
        amount: number | undefined,
        awbnr: string,
        sparePartText: string,
        strahler: string,
        returnBy: moment.Moment,
        orderNrOld: string,
        xRayDateOld: moment.Moment,
        repairPrice: number,
        customerPrice: number | undefined,
        customerCurrency: string,
        cost: number | undefined,
        tubeIndicator: string,
        radioActIndicator: string,
        serialPIndicator: string,
        batchNumber: string,
        localScrapReturnC: string,
        localScrapReturnD: string,
        localScrapReturnV: string,
        localScrapReturnX: string,
        lsCrChangeable: string,
        pos: number | undefined,
        currency: string,
        eodDate: moment.Moment,
        eosDate: moment.Moment,
        ucrNumber: string,
        unNumber: string,
        dangerousGoodsIndicator: string,
        hazardousMaterial: string,
        dangerousGoodsText = '',
        hazardousMaterialText = '',
        storageLocation = '',
        storageLocationDesc = '',
        billing = ConfigConstants.BILLABLE_CODE.AUTO,
        billingChangeable = '',
        serialNumberOld = '',
        serialNumberNew = '',
        textLine = '',
        repairable = '',
        qualityPart = '',
        batchNumberLegacy = '',
        spqDocID = '',
        enableFDA2579 = '',
        isInBootstock = '',
        medPhysicsFlag = '',
    ) {
        super('PartModel');
        this.materialIdentifier = new MaterialIdentifierModel(materialNr, orderNr, orderPos);
        this.prevOrder = <NumberModel>this.set(new NumberModel('PreviousOrder', prevOrder));
        this.descText = <TextModel>this.set(new TextModel('DescText', descText));
        this.quantity = <NumberModel>this.set(new NumberModel('Menge', quantity));
        this.matMerkmal = <TextModel>this.set(new TextModel('MatMerkmal', matMerkmal));
        this.extUnit = <TextModel>this.set(new TextModel('ExtUnit', extUnit));
        this.amount = <NumberModel>this.set(new NumberModel('Amount', amount));
        this.awbnr = <TextModel>this.set(new TextModel('Awbnr', awbnr));
        this.sparePartText = <TextModel>this.set(new TextModel('SparePartTxt', sparePartText));
        this.returnBy = <DateModel>this.set(new DateModel('RFrist', returnBy));
        this.orderNrOld = <TextModel>this.set(new TextModel('EbelnOld', orderNrOld));
        this.xRayDateOld = <DateModel>this.set(new DateModel('XRayDateOld', xRayDateOld));
        this.repairPrice = <NumberModel>this.set(new NumberModel('RepPrice', repairPrice));
        this.customerPrice = new NumberModel('CustomerPrice', customerPrice);
        this.customerCurrency = new TextModel('CustomerCurrency', customerCurrency);
        this.cost = <NumberModel>this.set(new NumberModel('Cost', cost));
        this.pos = <NumberModel>this.set(new NumberModel('Pos', pos));
        this.tubeIndicator = <TextModel>this.set(new TextModel('TubeIndicator', tubeIndicator));
        this.radioActIndicator = <TextModel>this.set(new TextModel('RadioActIndicator', radioActIndicator));
        this.serialPIndicator = <TextModel>this.set(new TextModel('SerialPIndicator', serialPIndicator));
        this.batchNumber = <TextModel>this.set(new TextModel('BatchId', batchNumber));
        this.localScrapReturnC = <TextModel>this.set(new TextModel('LocalScrapReturnC', localScrapReturnC));
        this.localScrapReturnD = <TextModel>this.set(new TextModel('LocalScrapReturnD', localScrapReturnD));
        this.localScrapReturnV = <TextModel>this.set(new TextModel('LocalScrapReturnV', localScrapReturnV));
        this.localScrapReturnX = <TextModel>this.set(new TextModel('LocalScrapReturnX', localScrapReturnX));
        this.lsCrChangeable = <TextModel>this.set(new TextModel('LsCrChangeable', lsCrChangeable));
        this.strahler = <TextModel>this.set(new TextModel('Strahler', strahler));
        this.currency = <TextModel>this.set(new TextModel('Currency', currency));
        this.eodDate = <DateModel>this.set(new DateModel('EodDate', eodDate));
        this.eosDate = <DateModel>this.set(new DateModel('EosDate', eosDate));
        this.ucrNumber = <TextModel>this.set(new TextModel('UCRNumber', ucrNumber));
        this.unNumber = unNumber;
        this.dangerousGoodsIndicator = <TextModel>(
            this.set(new TextModel('DangerousGoodsIndicator', dangerousGoodsIndicator))
        );
        this.hazardousMaterial = <TextModel>this.set(new TextModel('HazardousMaterial', hazardousMaterial));
        this.storageLocation = <TextModel>this.set(new TextModel('StorageLocation', storageLocation));
        this.storageLocationDesc = <TextModel>this.set(new TextModel('storageLocationDesc', storageLocationDesc));
        this.dangerousGoodsText = <TextModel>this.set(new TextModel('DangerousGoodsText', dangerousGoodsText));
        this.hazardousMaterialText = <TextModel>this.set(new TextModel('HazardousMaterialText', hazardousMaterialText));
        this.billing = billing;
        this.billingChangeable = billingChangeable !== ConfigConstants.MATERIAL_BILLABLE_CHANGEABLE.No;
        this.serialNumberOld = <TextModel>this.set(new TextModel('OldSerialNumber', serialNumberOld));
        this.serialNumberNew = <TextModel>this.set(new TextModel('SerialNumber', serialNumberNew));
        this.textLine = textLine;
        this.repairable = repairable === ConfigConstants.materialRepairableIndicator;
        this.qualityPart = qualityPart;
        this.batchNumberLegacy = batchNumberLegacy;
        this.spqDocID = spqDocID;
        this.enableFDA2579 = enableFDA2579 === ConfigConstants.sapTrue;
        this.isInBootstock = isInBootstock === ConfigConstants.sapTrue;
        this.isMedPhysicsRelevant = medPhysicsFlag === ConfigConstants.sapTrue;
    }

    public static fromOData(part: ODataPartModel): PartModel {
        return new PartModel(
            part.PONumber,
            part.ItemNumber,
            part.Material,
            parseInt(part.PreviousOrder, 10),
            part.DescText,
            parseInt(part.Quantity, 10),
            part.MatMerkmal,
            part.ExtUnit,
            parseInt(part.Amount, 10),
            part.Awbnr,
            part.SparepartTxt,
            part.Strahler,
            moment(part.RFrist),
            part.PONumberOld,
            moment(part.XRayDateOld),
            parseFloat(part.RepPrice),
            parseFloat(part.CustomerPrice),
            part.CustomerCurrency,
            parseFloat(part.Cost),
            part.TubeIndicator,
            part.RadioactIndicat,
            part.SerialPIndicat,
            part.BatchNumber,
            part.LocalScrapReturnC,
            part.LocalScrapReturnD,
            part.LocalScrapReturnV,
            part.LocalScrapReturnX,
            part.LsCrChangeable,
            parseInt(part.Counter, 10),
            part.Currency,
            moment(part.EodDate),
            moment(part.EosDate),
            part.UCRNumber,
            part.UNNumber,
            part.DangerousGoodsIndicator,
            part.HazardousMaterial,
            part.PROFText,
            part.STOFText,
            '',
            '',
            <ConfigConstants.BILLABLE_CODE>part.Billing,
            part.BillingChangeable,
            part.SerialNumberOld,
            part.SerialNumberNew,
            part.TextLine,
            part.Repairable,
            part.QualityPart,
            part.BatchNumberLegacy,
            part.SPQDocID,
            part.EnableFDA2579,
            // This is "isInBootstock". But this field is non-existent when used to create a PartModel from a NdpHeaderSet.PartSet.
            // Whereas it is existent when used for MatStockSet.MatStockAvailabilitySet. Have to set hard-coded empty value here.
            '',
            part.MedPhysicsFlag,
        );
    }

    public static fromODataMatStockAvailabilityModel(stockMaterial: ODataMatStockAvailabilityModel): PartModel {
        return new PartModel(
            stockMaterial.PONumber,
            stockMaterial.ItemNumber,
            stockMaterial.Material,
            undefined,
            stockMaterial.MaterialDesc,
            parseInt(stockMaterial.Quantity, 10),
            '',
            stockMaterial.ExtUnit,
            undefined,
            '',
            '',
            '',
            moment.invalid(),
            '',
            moment.invalid(),
            parseInt(stockMaterial.CustomerPrice, 10),
            undefined,
            '',
            undefined,
            '',
            '',
            stockMaterial.SerialPIndicat,
            stockMaterial.BatchNumber,
            stockMaterial.LocalScrapReturnC,
            stockMaterial.LocalScrapReturnD,
            stockMaterial.LocalScrapReturnV,
            stockMaterial.LocalScrapReturnX,
            stockMaterial.LsCrChangeable,
            undefined,
            stockMaterial.CustomerCurrency,
            moment.invalid(),
            moment.invalid(),
            stockMaterial.UCRNumber ?? '',
            '',
            stockMaterial.DangerousGoodsIndicator,
            stockMaterial.HazardousMaterial,
            '',
            '',
            stockMaterial.StorageLoc,
            stockMaterial.StorageLocDesc,
            ConfigConstants.BILLABLE_CODE.AUTO,
            '',
            '',
            '',
            '',
            '',
            '',
            stockMaterial.BatchNumberLegacy ?? '',
            '',
            '',
            stockMaterial.TrunkStck ?? '',
        );
    }

    public static build({
        orderNr = '',
        orderPos = '',
        materialNr = '',
        prevOrder = NaN,
        descText = '',
        quantity = NaN,
        matMerkmal = '',
        extUnit = '',
        amount = NaN,
        awbnr = '',
        sparePartText = '',
        strahler = ' ',
        returnBy = moment.invalid(),
        orderNrOld = '',
        xRayDateOld = moment.invalid(),
        repairPrice = NaN,
        customerPrice = NaN,
        customerCurrency = '',
        cost = NaN,
        tubeIndicator = '',
        radioActIndicator = '',
        serialPIndicator = '',
        batchNumber = '',
        localScrapReturnC = '',
        localScrapReturnD = '',
        localScrapReturnV = '',
        localScrapReturnX = '',
        lsCrChangeable = '',
        pos = NaN,
        currency = '',
        eodDate = moment.invalid(),
        eosDate = moment.invalid(),
        ucrNumber = '',
        unNumber = '',
        dangerousGoodsIndicator = '',
        hazardousMaterial = '',
        dangerousGoodsText = '',
        hazardousMaterialText = '',
        storageLocation = '',
        storageLocationDesc = '',
        billing = ConfigConstants.BILLABLE_CODE.AUTO,
        billingChangeable = '',
        serialNumberOld = '',
        serialNumberNew = '',
        textLine = '',
        repairable = '',
        qualityPart = '',
        batchNumberLegacy = '',
        spqDocId = '',
        isInBootstock = '',
    }: {
        orderNr?: string;
        orderPos?: string;
        materialNr?: string;
        prevOrder?: number;
        descText?: string;
        quantity?: number;
        matMerkmal?: string;
        extUnit?: string;
        amount?: number;
        awbnr?: string;
        sparePartText?: string;
        strahler?: string;
        returnBy?: moment.Moment;
        orderNrOld?: string;
        xRayDateOld?: moment.Moment;
        repairPrice?: number;
        customerPrice?: number;
        customerCurrency?: string;
        cost?: number;
        tubeIndicator?: string;
        radioActIndicator?: string;
        serialPIndicator?: string;
        batchNumber?: string;
        localScrapReturnC?: string;
        localScrapReturnD?: string;
        localScrapReturnV?: string;
        localScrapReturnX?: string;
        lsCrChangeable?: string;
        pos?: number;
        currency?: string;
        eodDate?: moment.Moment;
        eosDate?: moment.Moment;
        ucrNumber?: string;
        unNumber?: string;
        dangerousGoodsIndicator?: string;
        hazardousMaterial?: string;
        dangerousGoodsText?: string;
        hazardousMaterialText?: string;
        storageLocation?: string;
        storageLocationDesc?: string;
        billing?: ConfigConstants.BILLABLE_CODE;
        billingChangeable?: string;
        serialNumberOld?: string;
        serialNumberNew?: string;
        textLine?: string;
        repairable?: string;
        qualityPart?: string;
        batchNumberLegacy?: string;
        spqDocId?: string;
        isInBootstock?: string;
    }): PartModel {
        return new PartModel(
            orderNr,
            orderPos,
            materialNr,
            prevOrder,
            descText,
            quantity,
            matMerkmal,
            extUnit,
            amount,
            awbnr,
            sparePartText,
            strahler,
            returnBy,
            orderNrOld,
            xRayDateOld,
            repairPrice,
            customerPrice,
            customerCurrency,
            cost,
            tubeIndicator,
            radioActIndicator,
            serialPIndicator,
            batchNumber,
            localScrapReturnC,
            localScrapReturnD,
            localScrapReturnV,
            localScrapReturnX,
            lsCrChangeable,
            pos,
            currency,
            eodDate,
            eosDate,
            ucrNumber,
            unNumber,
            dangerousGoodsIndicator,
            hazardousMaterial,
            dangerousGoodsText,
            hazardousMaterialText,
            storageLocation,
            storageLocationDesc,
            billing,
            billingChangeable,
            serialNumberOld,
            serialNumberNew,
            textLine,
            repairable,
            qualityPart,
            batchNumberLegacy,
            spqDocId,
            isInBootstock,
        );
    }
}

export interface ODataPartModel extends ODataObject {
    Amount: string;
    Awbnr: string;
    BatchNumber: string;
    BatchNumberLegacy: string;
    Billing: string;
    BillingChangeable: string;
    Cost: string;
    Counter: string;
    Currency: string;
    CustomerCurrency: string;
    CustomerPrice: string;
    DangerousGoodsIndicator: string;
    DescText: string;
    EodDate: string;
    EosDate: string;
    ExtUnit: string;
    HazardousMaterial: string;
    ItemNumber: string;
    LocalScrapReturnC: string;
    LocalScrapReturnD: string;
    LocalScrapReturnV: string;
    LocalScrapReturnX: string;
    LsCrChangeable: string;
    Material: string;
    MatMerkmal: string;
    PONumber: string;
    PONumberOld: string;
    PreviousOrder: string;
    PROFText: string;
    QualityPart: string;
    Quantity: string;
    RadioactIndicat: string;
    Repairable: string;
    RepPrice: string;
    RFrist: string;
    SerialNumberNew: string;
    SerialNumberOld: string;
    SerialPIndicat: string;
    SparepartTxt: string;
    SPQDocID: string;
    STOFText: string;
    Strahler: string;
    TextLine: string;
    TubeIndicator: string;
    UCRNumber: string;
    UNNumber: string;
    XRayDateOld: string;
    EnableFDA2579: string;
    MedPhysicsFlag: string;
    __metadata: {
        uri: string;
        type: 'HC_SM_NGM_58_SRV.Part';
    };
}
